'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { getProductWithOption, listProductsWithOptions } from '@/app/apis';
import { PRODUCTS_LIST_DEFAULT_DATA, SITE, TEAM_PRODUCTS_LIST_DEFAULT_DATA } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { SubscriptionInfo } from '@/app/stores/slices/user-info';
import type {
  ClawPassProduct,
  ClawPassType,
  CreditsPurchaseProduct,
  CreditV1CreditsPurchaseProduct,
  CreditV1PeriodProduct,
  CreditV1TeamBundleProduct,
  PaidTrialInfo,
  PeriodType,
  ProductList,
  ProductPayStatusType,
  ProductPlan,
  SiteType,
  SubsPlanType,
  TabItemType,
  TeamPeriodType,
  TeamProductList,
  TeamTabItemType,
  TeamTier,
  UserInfo,
} from '@/app/types';
import {
  getClawPassProductStatus,
  getMaxProductDiscount,
  getPaidTrialInfo,
  getPriceDiscount,
  getProductStatus,
} from '@/app/utils';
import { useBonusCode } from './use-bonus-code';
import { useFetch } from './use-fetch';

export interface SetCurrentCouponProps {
  product_type: string;
  productId: number;
  couponId: string;
}

// 个人版 ProductList 接收的合法 plan_type（不含 team_v2：企业版按 tier 独立建模，见 buildTeamProductMap）
const VALID_PLAN_TYPES = new Set(['free', 'standard_v2', 'premium_v2', 'ultimate_v2']);
const VALID_BILLING_PERIODS = new Set(['monthly', 'quarterly', 'yearly']);
const VALID_PRODUCT_TYPES = new Set(['pass', 'subscription']);
/** Synthetic PeriodType value used only on the client for the paid-trial tab. */
export const PAID_TRIAL_PERIOD = 'paid_trial' as PeriodType;

// 企业版按 tier 分档（与个人版按 plan_type 分档对应），billing_period 只支持季 / 年
const VALID_TEAM_TIERS = new Set<TeamTier>(['basic', 'plus', 'pro']);
const VALID_TEAM_BILLING_PERIODS = new Set(['quarterly', 'yearly']);

interface BuildProductMapResult {
  products: ProductList;
  /** 每个 period 下的最大优惠百分比，如 30 表示最多优惠 30%（即 7 折） */
  maxPercentOffByPeriod: Record<PeriodType, number>;
  initialPeriod: PeriodType | null;
  periodKeys: PeriodType[];
}

/**
 * 遍历原始产品列表，构建按 period 分组的 ProductList，
 * 同时收集每个 period 的最大优惠百分比和当前订阅对应的 initialPeriod。
 *
 * percentOff 含义：30 = 优惠 30%（原价打 7 折），由 getPriceDiscount 计算。
 */
function buildProductMap(
  productList: CreditV1PeriodProduct[],
  subscription: SubscriptionInfo | null,
  userInfo: UserInfo | null,
  clawPass: ClawPassType | null,
): BuildProductMapResult {
  let initialPeriod: PeriodType | null = null;
  const periodKeys: PeriodType[] = [];
  const maxPercentOffByPeriod: Record<PeriodType, number> = {
    subscription_monthly: 0,
    subscription_quarterly: 0,
    subscription_yearly: 0,
    pass_monthly: 0,
    pass_quarterly: 0,
    pass_yearly: 0,
  };
  const products = { ...PRODUCTS_LIST_DEFAULT_DATA };

  for (const product of productList) {
    const { product_type, billing_period, plan_type, id } = product;

    if (
      !VALID_PRODUCT_TYPES.has(product_type) ||
      !VALID_BILLING_PERIODS.has(billing_period) ||
      !VALID_PLAN_TYPES.has(plan_type)
    ) {
      continue;
    }

    const key = `${product_type}_${billing_period}` as PeriodType;

    if (subscription?.product_id === id) {
      initialPeriod = key;
    }

    // percentOff: 30 = 优惠 30%（中文 7 折），赋值给产品的 discount 字段（类型约束来自 SubscriptionProductType）
    const percentOff = getPriceDiscount(product.price ?? 0, product.original_price ?? 0);
    products[key][plan_type as keyof ProductPlan] = {
      ...product,
      discount: percentOff,
      product_pay_status: getProductStatus(product, subscription, clawPass),
      plan_type: plan_type as SubsPlanType,
    };

    if (percentOff > maxPercentOffByPeriod[key]) {
      maxPercentOffByPeriod[key] = percentOff;
    }

    if (!periodKeys.includes(key)) {
      periodKeys.push(key);
      fillPlaceholderPlans(products, key, product, subscription, userInfo);
    }
  }

  // 年付产品的折扣率应体现"相对月付原价"的综合优惠（年付优惠 + 券折扣叠加），
  // 而非仅相对年付自身 original_price（那只反映券折扣）。
  // 用同 plan 月付产品的 original_price × 12 做分母重算。
  const yearlyKeys: PeriodType[] = ['subscription_yearly', 'pass_yearly'];
  const monthlyKeys: Record<string, PeriodType> = {
    subscription_yearly: 'subscription_monthly',
    pass_yearly: 'pass_monthly',
  };
  for (const yk of yearlyKeys) {
    const mk = monthlyKeys[yk];
    const yearlyPlans = products[yk];
    const monthlyPlans = products[mk];
    if (!yearlyPlans || !monthlyPlans) continue;
    let maxOff = 0;
    for (const planKey of Object.keys(yearlyPlans) as (keyof ProductPlan)[]) {
      const yearlyProduct = yearlyPlans[planKey];
      const monthlyProduct = monthlyPlans[planKey];
      if (!yearlyProduct || !monthlyProduct) continue;
      const monthlyBaseline = (monthlyProduct.original_price ?? monthlyProduct.price) * 12;
      if (monthlyBaseline > 0 && yearlyProduct.price < monthlyBaseline) {
        const combinedOff = 100 - Math.round((yearlyProduct.price / monthlyBaseline) * 100);
        if (combinedOff > maxOff) maxOff = combinedOff;
      }
    }
    if (maxOff > maxPercentOffByPeriod[yk]) {
      maxPercentOffByPeriod[yk] = maxOff;
    }
  }

  return { products, maxPercentOffByPeriod, initialPeriod, periodKeys };
}

/**
 * 为指定 period 补充 free 占位卡片。
 *
 * 注：team_v2 占位卡片已下线，企业版套餐改由后端 team_products 字段单独返回，
 * 由调用方根据 edition 切换渲染。
 */
function fillPlaceholderPlans(
  products: ProductList,
  key: PeriodType,
  templateProduct: CreditV1PeriodProduct,
  subscription: SubscriptionInfo | null,
  userInfo: UserInfo | null,
) {
  products[key].free = {
    ...templateProduct,
    sub_change_id: '0',
    price: 0,
    original_price: 0,
    available: false,
    plan_type: 'free',
    product_pay_status: !subscription?.product_id && userInfo?.subs_plan !== 'team_v2' ? 'current' : 'unavailable',
    discount: 0,
  };
}

function formatCreditsProducts(raw: CreditV1CreditsPurchaseProduct[]): CreditsPurchaseProduct[] {
  return raw.map((v) => ({ ...v, product_pay_status: 'available' as const }));
}

interface BuildTeamProductMapResult {
  products: TeamProductList;
  /** 每个 team period 下的最大优惠百分比，如 36 表示最多 36% off */
  maxPercentOffByPeriod: Record<TeamPeriodType, number>;
  initialPeriod: TeamPeriodType | null;
  periodKeys: TeamPeriodType[];
}

/**
 * 与 buildProductMap 结构对齐的企业版分组器：
 * - 按 `team_${billing_period}` 拆 period（命名空间用 team_ 前缀，避免与个人版 PeriodType 冲突）；
 * - 第二层用 `tier`（basic/plus/pro）作为索引，因为企业版所有 SKU 的 plan_type 都是 team_v2；
 * - product_pay_status 由本函数统一派生，UI 只看该字段，无需再读 store 里的 activeBundle：
 *   - `current`：tier + billing_period 与 activeBundle 完全匹配；
 *   - `unavailable`：已持有任一团队 bundle（其他档位/周期不可重复购买）或后端显式 available=false；
 *   - `available`：可购买。
 * - 命中 current 的 period 会作为 initialPeriod，让默认 tab 落到当前持有的周期。
 */
function buildTeamProductMap(
  productList: CreditV1PeriodProduct[],
  activeBundle: CreditV1TeamBundleProduct | null,
): BuildTeamProductMapResult {
  const periodKeys: TeamPeriodType[] = [];
  const maxPercentOffByPeriod: Record<TeamPeriodType, number> = {
    team_yearly: 0,
    team_quarterly: 0,
  };
  const products: TeamProductList = {
    team_yearly: { ...TEAM_PRODUCTS_LIST_DEFAULT_DATA.team_yearly },
    team_quarterly: { ...TEAM_PRODUCTS_LIST_DEFAULT_DATA.team_quarterly },
  };

  const activeTier = activeBundle?.tier;
  const activeBillingPeriod = activeBundle?.billing_period;
  const hasActiveBundle = Boolean(activeTier && activeTier !== 'unspecified');
  let initialPeriod: TeamPeriodType | null = null;

  for (const product of productList) {
    const { billing_period } = product;
    const tier = product.tier as TeamTier | undefined;

    if (!tier || !VALID_TEAM_TIERS.has(tier) || !VALID_TEAM_BILLING_PERIODS.has(billing_period)) {
      continue;
    }

    const key = `team_${billing_period}` as TeamPeriodType;
    const isCurrent = hasActiveBundle && activeTier === tier && activeBillingPeriod === billing_period;
    const status: ProductPayStatusType = isCurrent
      ? 'current'
      : hasActiveBundle || product.available === false
        ? 'unavailable'
        : 'available';

    if (isCurrent) initialPeriod = key;

    const percentOff = getPriceDiscount(product.price ?? 0, product.original_price ?? 0);
    products[key][tier] = {
      ...product,
      discount: percentOff,
      product_pay_status: status,
      plan_type: product.plan_type as SubsPlanType,
    };

    if (percentOff > maxPercentOffByPeriod[key]) {
      maxPercentOffByPeriod[key] = percentOff;
    }

    if (!periodKeys.includes(key)) {
      periodKeys.push(key);
    }
  }

  return { products, maxPercentOffByPeriod, initialPeriod, periodKeys };
}

interface SubscriptionData {
  products: ProductList | null;
  tabList: TabItemType[];
  initialPeriod: PeriodType | null;
  /** Present when any subscription_products entry has paid_trial. */
  paidTrialInfo: PaidTrialInfo | null;
}

const INITIAL_SUBSCRIPTION_DATA: SubscriptionData = {
  products: null,
  tabList: [],
  initialPeriod: null,
  paidTrialInfo: null,
};

interface TeamSubscriptionData {
  products: TeamProductList | null;
  tabList: TeamTabItemType[];
  initialPeriod: TeamPeriodType | null;
}

const INITIAL_TEAM_SUBSCRIPTION_DATA: TeamSubscriptionData = {
  products: null,
  tabList: [],
  initialPeriod: null,
};

export const useFetchProductList = () => {
  const t = useTranslations();

  const {
    bonusCode,
    subscription,
    activeTeamBundleProduct,
    refreshGetProducts,
    updateRefreshGetProducts,
    updateMaxProductDiscount,
    userInfo,
    clawPass,
  } = useStore((state) => ({
    bonusCode: state.bonusCode,
    subscription: state.subscription,
    activeTeamBundleProduct: state.activeTeamBundleProduct,
    refreshGetProducts: state.refreshGetProducts,
    updateRefreshGetProducts: state.updateRefreshGetProducts,
    updateMaxProductDiscount: state.updateMaxProductDiscount,
    userInfo: state.userInfo,
    clawPass: state.clawPass,
  }));

  const { handleBonusCode } = useBonusCode();

  const [creditsProducts, setCreditsProducts] = useState<CreditsPurchaseProduct[]>([]);
  const [clawPassProducts, setClawPassProducts] = useState<ClawPassProduct[]>([]);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData>(INITIAL_SUBSCRIPTION_DATA);
  const [teamSubscriptionData, setTeamSubscriptionData] =
    useState<TeamSubscriptionData>(INITIAL_TEAM_SUBSCRIPTION_DATA);

  /**
   * 根据接口返回的优惠数据生成 tab 列表。
   * percentOff: 30 = 优惠 30%（中文显示"最低7折"）。
   */
  const buildTabList = (periodKeys: PeriodType[], maxPercentOffByPeriod: Record<PeriodType, number>): TabItemType[] => {
    const tabTemplates: Record<SiteType, TabItemType[]> = {
      china: [
        { value: 'pass_yearly', label: '按年购买' },
        { value: 'subscription_monthly', label: '连续包月' },
        { value: 'pass_monthly', label: '按月购买' },
      ],
      global: [
        { value: 'subscription_yearly', label: t('payments.plans.subscriptionYear') },
        { value: 'subscription_monthly', label: t('payments.plans.subscriptionMonth') },
      ],
    };

    return tabTemplates[SITE]
      .filter((tab) => periodKeys.includes(tab.value))
      .map((tab) => {
        const percentOff = maxPercentOffByPeriod[tab.value];
        if (percentOff > 0) {
          return {
            ...tab,
            // percentOff=30 → (100-30)/10=7 → "最低7折"
            tooltip:
              SITE === 'china' ? `最低${(100 - percentOff) / 10}折` : t('payments.plans.off', { num: percentOff }),
            isDiscount: true,
            discount: percentOff,
          };
        }
        return { ...tab };
      });
  };

  /**
   * 企业版 tab list 构建：
   * 与个人版 buildTabList 同形——按可用 period 过滤模板、按 maxPercentOff 注入 tooltip。
   * 视觉文案统一走 i18n（billingYearly / billingQuarterly），不区分 site。
   */
  const buildTeamTabList = (
    periodKeys: TeamPeriodType[],
    maxPercentOffByPeriod: Record<TeamPeriodType, number>,
  ): TeamTabItemType[] => {
    const tabTemplates: TeamTabItemType[] = [
      { value: 'team_yearly', label: t('payments.plans.billingYearly') },
      { value: 'team_quarterly', label: t('payments.plans.billingQuarterly') },
    ];

    return tabTemplates
      .filter((tab) => periodKeys.includes(tab.value))
      .map((tab) => {
        const percentOff = maxPercentOffByPeriod[tab.value];
        if (percentOff > 0) {
          return {
            ...tab,
            tooltip:
              SITE === 'china' ? `最低${(100 - percentOff) / 10}折` : t('payments.plans.off', { num: percentOff }),
            isDiscount: true,
            discount: percentOff,
          };
        }
        return { ...tab };
      });
  };

  useEffect(() => {
    if (refreshGetProducts) {
      updateRefreshGetProducts(false);
      getProductsList();
    }
  }, [refreshGetProducts]);

  const {
    loading: loadingProducts,
    error,
    run: getProductsList,
  } = useFetch(
    () =>
      listProductsWithOptions({
        query: {
          channels: SITE === 'global' ? ['stripe'] : ['alipay'],
          bonus_code: bonusCode || undefined,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (!data) return;

        setCreditsProducts(formatCreditsProducts(data.credits_purchase_products ?? []));
        setClawPassProducts(
          (data.claw_pass_products ?? []).map((v) => ({
            ...v,
            product_pay_status: getClawPassProductStatus(v),
            plan_type: v.plan_type as SubsPlanType,
          })),
        );

        const rawProducts = [...(data.subscription_products ?? []), ...(data.pass_products ?? [])];
        // 兑换/切换券后重新拉列表时，同步刷新营销文案的「最高X折」（年对月叠加券取最大）
        updateMaxProductDiscount(getMaxProductDiscount(rawProducts));
        const { products, maxPercentOffByPeriod, initialPeriod, periodKeys } = buildProductMap(
          rawProducts,
          subscription,
          userInfo,
          clawPass,
        );
        const tabList = buildTabList(periodKeys, maxPercentOffByPeriod);

        // 试用档判定与开屏弹窗共用同一函数，避免两处口径漂移
        const paidTrialInfo = getPaidTrialInfo(data.subscription_products ?? []);

        // Inject the paid-trial tab when eligible
        const finalTabList: TabItemType[] = paidTrialInfo
          ? [
              ...tabList,
              {
                value: PAID_TRIAL_PERIOD,
                label: t('payments.plans.newUserTrial'),
                paidTrial: paidTrialInfo,
              },
            ]
          : tabList;

        setSubscriptionData({
          products,
          tabList: finalTabList,
          // 有试用时优先默认选中试用 tab，否则按订阅状态或第一个 tab
          initialPeriod: (paidTrialInfo ? PAID_TRIAL_PERIOD : null) ?? initialPeriod ?? finalTabList[0]?.value ?? null,
          paidTrialInfo,
        });

        // 企业版按"次包/新购"语义：不参与升降级，已订阅同 SKU 即 current、其余统一 unavailable / available。
        // pay() 据此走 createNewOrder，跳过 ChangeContent 升降级变更弹窗。
        const {
          products: teamProducts,
          maxPercentOffByPeriod: teamMaxPercentOffByPeriod,
          initialPeriod: teamInitialPeriod,
          periodKeys: teamPeriodKeys,
        } = buildTeamProductMap(data.team_products ?? [], activeTeamBundleProduct);
        const teamTabList = buildTeamTabList(teamPeriodKeys, teamMaxPercentOffByPeriod);
        setTeamSubscriptionData({
          products: teamProducts,
          tabList: teamTabList,
          initialPeriod: teamInitialPeriod ?? teamTabList[0]?.value ?? null,
        });

        updateRefreshGetProducts(false);
        if (data.bonus_code && SITE === 'china') handleBonusCode(data.bonus_code);
      },
    },
  );

  const { runAsync: getProductsListWithCouponAsync } = useFetch(
    (productId: number, couponId: string) =>
      getProductWithOption({ path: { product_id: productId }, query: { coupon_id: couponId } }),
    { manual: true },
  );

  const setCurrentCoupon = ({ product_type, productId, couponId }: SetCurrentCouponProps) => {
    getProductsListWithCouponAsync(productId, couponId).then(({ data }) => {
      if (product_type === 'credits_purchase') {
        setCreditsProducts((prev) =>
          prev.map((item) =>
            item.id === productId
              ? {
                  ...item,
                  ...data,
                  available_coupons: item.available_coupons?.map((c) => ({ ...c, is_best: c.id === couponId })),
                }
              : item,
          ),
        );
      }
    });
  };

  return {
    getProductsList,
    loading: loadingProducts,
    error,
    creditsProducts,
    clawPassProducts,
    subscriptionProducts: subscriptionData.products,
    subscriptionTabList: subscriptionData.tabList,
    subscriptionInitialPeriod: subscriptionData.initialPeriod,
    subscriptionPaidTrialInfo: subscriptionData.paidTrialInfo,
    teamSubscriptionProducts: teamSubscriptionData.products,
    teamSubscriptionTabList: teamSubscriptionData.tabList,
    teamSubscriptionInitialPeriod: teamSubscriptionData.initialPeriod,
    setCurrentCoupon,
  };
};
